import { z } from "zod";

import { configValueSchema, type ConfigValue } from "./configValue.ts";

/**
 * A narrowing of a value beyond its base type — an enum is a string restricted
 * to a list, a colour is a string, a bounded number is a number — declared on a
 * `ValueDescriptor` so pickers offer only compatible values and a binding cannot
 * be made across incompatible subtypes.
 *
 * Pure serializable data: `id` names the narrowing and the remaining properties
 * are its parameters. `id` is an open string, and the package that introduces a
 * subtype owns its id, its constructor and the requirement that reads its
 * parameters — the data layer knows none of them. Core owns the few it declares
 * on its own external values, and nothing beyond those.
 *
 * Design-time only. The base `type` stays the only type information that reaches
 * runtime, where coercion is base-type.
 */
export interface ValueSubtype {
  id: string;
  /** Subtype-specific parameters, read by the requirement that knows the id. */
  [param: string]: ConfigValue;
}

export const ValueSubtype = z
  .object({ id: z.string().min(1) })
  .catchall(configValueSchema) satisfies z.ZodType<ValueSubtype>;
